import {
  Button,
  Container,
  Divider,
  Group,
  LoadingOverlay,
  NumberInput,
  Select,
  Switch,
  Text,
  useComputedColorScheme,
  useMantineColorScheme,
  useMantineTheme,
  Card,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { useState } from "react";
import { HiCog, HiHome, HiRefresh, HiTemplate } from "react-icons/hi";
import { exampleGalleryFiles } from "../../examples/files";
import Gallery from "../gallery/Gallery";
import useGlobalState, { globalSettings } from "../../globalState";
import { SETTINGS_DEFAULTS, Settings as SettingsType } from "../../settings";
import AppCard from "../common/AppCard";

interface SettingsItem {
  title: string;
  description: string | null;
  settingsKey: string | null;
  type: "switch" | "number" | "numberWithUnit" | "select";
  options?: { value: string; label: string }[];
  getDefaultValue?: () => any;
  mapValueToDisplay?: (value: any) => any;
  mapValueToStore?: (value: any) => any;
  unit?: string;
  min?: number;
  max?: number;
  action?: (value: any) => void;
  conditionalOn?: (settings: SettingsType) => boolean;
}

interface SettingsSection {
  title: string;
  icon: React.ReactNode;
  description: string | null;
  items: SettingsItem[];
  renderExample?: () => React.ReactNode;
}

export default function Settings() {
  const { setColorScheme, clearColorScheme } = useMantineColorScheme({
    keepTransitions: true,
  });
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const [settings, setSettings] = useGlobalState<SettingsType>(globalSettings);
  const colorScheme = useComputedColorScheme();

  const [shouldRerender, setShouldRerender] = useState(false);

  const settingsSections: SettingsSection[] = [
    {
      title: "General",
      icon: <HiCog />,
      description: null,
      items: [
        {
          settingsKey: null,
          title: "Dark mode",
          description: "Toggle dark mode for all app",
          getDefaultValue: () => colorScheme === "dark",
          action: () => {
            setColorScheme(colorScheme === "dark" ? "light" : "dark");
          },
          type: "switch",
        },
      ],
    },
    {
      title: "Home",
      icon: <HiHome />,
      description: "Home page settings",
      items: [
        {
          title: "Locations",
          description: "Set the location card style",
          settingsKey: "home_globeCard_type",
          type: "select",
          options: [
            { value: "animated", label: "Animated location showcase" },
            { value: "interactive", label: "Interactive globe" },
          ],
        },
      ],
    },
    {
      title: "Gallery",
      icon: <HiTemplate />,
      description: "Image visualization and gallery settings",
      items: [
        {
          title: "Layout",
          description: "Set the layout type",
          settingsKey: "photoAlbum_layout",
          type: "select",
          options: [
            { value: "rows", label: "Rows" },
            { value: "columns", label: "Columns" },
            { value: "masonry", label: "Masonry" },
          ],
        },
        {
          title: "Row height",
          description: "Set the target row height",
          settingsKey: "photoAlbum_targetRowHeight",
          type: "number",
          conditionalOn: (settings) => settings.photoAlbum_layout === "rows",
        },
        {
          title: "Spacing",
          description: "Set the spacing between images",
          settingsKey: "photoAlbum_spacing",
          type: "number",
          min: 0,
          max: 50,
        },
        {
          title: "Slideshow delay",
          description: "The delay between slides in the slideshow",
          settingsKey: "slideshow_delay",
          type: "numberWithUnit",
          mapValueToDisplay: (value) => value / 1000,
          mapValueToStore: (value) => value * 1000,
          unit: "seconds",
          min: 0,
          max: 1000000,
        },
        {
          title: "Rounded border",
          description: "Toggle rounded border for all images",
          settingsKey: "photoAlbum_roundedBorder",
          type: "switch",
        },
        {
          title: "Thumbnail hover effect",
          description: "Toggle thumbnail hover effect",
          settingsKey: "gallery_thumbnailHoverEffect",
          type: "switch",
        }
      ],
      renderExample: () => (
        <div className="flex flex-col pt-4">
          <Gallery
            isMiniGallery={true}
            folders={[]}
            files={exampleGalleryFiles as any}
            loadMoreFn={() => {}}
            onFolderClickFn={() => {}}
            onActionClick={() => {}}
            isSelectionEnabled={false}
            demo={true}
          ></Gallery>
        </div>
      ),
    },
  ];

  const onSettingChange = (item, value) => {
    if (!!item.action) {
      item.action();
    }

    // Save setting to localStorage
    if (!item?.settingsKey) return;
    const storedSettings = localStorage.getItem("settings");

    if (
      storedSettings != null &&
      storedSettings !== "" &&
      storedSettings !== undefined
    ) {
      const settingsObj = JSON.parse(storedSettings);

      if (item?.mapValueToStore) {
        value = item.mapValueToStore(value);
      }

      settingsObj[item.settingsKey] = value;

      localStorage.setItem("settings", JSON.stringify(settingsObj));
      setSettings({
        ...settings,
        [item.settingsKey]: value,
      });
    } else {
      localStorage.setItem(
        "settings",
        JSON.stringify({ [item.settingsKey]: value })
      );
      setSettings({ ...settings, [item.settingsKey]: value });
    }
  };

  const getDefaultValue = (item) => {
    if (item?.getDefaultValue) {
      return item.getDefaultValue();
    }
    if (item?.settingsKey && settings[item.settingsKey]) {
      if (item?.mapValueToDisplay) {
        return item.mapValueToDisplay(settings[item.settingsKey]);
      }
      return settings[item.settingsKey];
    }
    return null;
  };

  const renderSettingItem = (item) => {
    const inputWidth = "8rem"; // Set a consistent width for all inputs

    if (item.type === "switch") {
      return (
        <div className="flex flex-row gap-2">
          <Switch
            onLabel="ON"
            offLabel="OFF"
            className="switch"
            size="md"
            defaultChecked={getDefaultValue(item)}
            disabled={item["disabled"]}
            onChange={(value) =>
              onSettingChange(item, value.currentTarget.checked)
            }
          />
        </div>
      );
    }

    if (item.type === "number") {
      return (
        <div className="flex flex-row gap-2">
          <NumberInput
            className="numberInput"
            size="md"
            w={inputWidth}
            defaultValue={getDefaultValue(item)}
            disabled={item["disabled"]}
            onChange={(value) => onSettingChange(item, value)}
            stepHoldDelay={500}
            stepHoldInterval={100}
            min={item.min}
            max={item.max}
          />
        </div>
      );
    }

    if (item.type === "numberWithUnit") {
      return (
        <div className="flex flex-row gap-2">
          <NumberInput
            className="numberInput"
            size="md"
            w={inputWidth}
            defaultValue={getDefaultValue(item)}
            suffix={` ${item.unit}`}
            disabled={item["disabled"]}
            onChange={(value) => onSettingChange(item, value)}
            min={item.min}
            max={item.max}
            clampBehavior="strict"
            stepHoldDelay={500}
          />
        </div>
      );
    }

    if (item.type === "select") {
      return (
        <div className="flex flex-row gap-2">
          <Select
            className="select"
            size="md"
            w={inputWidth}
            defaultValue={getDefaultValue(item)}
            disabled={item["disabled"]}
            onChange={(_value, option) => onSettingChange(item, _value)}
            data={item.options}
          />
        </div>
      );
    }

    return <></>;
  };

  const resetDefaultSettings = () => {
    setSettings(SETTINGS_DEFAULTS);
    localStorage.removeItem("settings");
    setShouldRerender(true);
  };

  const mapItem = (item) => (
    <Group
      justify="space-between"
      className="item"
      wrap="nowrap"
      gap="xl"
      mb="sm"
      key={item.title}
    >
      <div className="w-3/5">
        <Text>{item.title}</Text>
        <Text size="xs" c="dimmed">
          {item.description}
        </Text>
      </div>
      {renderSettingItem(item)}
    </Group>
  );

  if (shouldRerender) {
    setTimeout(() => {
      setShouldRerender(false);
    }, 500);
    return (
      <div className="flex flex-col gap-2 h-48">
        <LoadingOverlay
          visible={true}
          zIndex={400}
          overlayProps={{
            blur: "xl",
            color: colorScheme === "dark" ? "black" : "black",
            opacity: colorScheme === "dark" ? "70%" : "30%",
          }}
          loaderProps={{ color: "blue", type: "oval", size: "md" }}
        />
      </div>
    );
  }

  return (
    <Container
      className="w-full mx-auto"
      p={isMobile ? "md" : "xl"}
      maw={"40rem"}
    >
      <Group justify="space-between" align="center" mb="lg">
        <Text fz="xl" className="title" fw={500}>
          Settings
        </Text>
        <Tooltip
          label="Reset to default settings"
          color="var(--mantine-color-dark-9)"
        >
          <ActionIcon
            variant="subtle"
            onClick={resetDefaultSettings}
            size="lg"
            color="gray"
          >
            <HiRefresh size={20} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {settingsSections.map((section) => (
        <AppCard mb="lg" key={section.title}>
          <Card.Section withBorder inheritPadding py="xs">
            <Group justify="space-between">
              <Group className="flex flex-row justify-start items-center gap-2">
                {section.icon}
                <Text fw={500}>{section.title}</Text>
              </Group>
            </Group>
          </Card.Section>

          {section?.description && (
            <Card.Section inheritPadding pt="sm">
              <Text fz="md" c="dimmed">
                {section.description}
              </Text>
            </Card.Section>
          )}

          <Card.Section inheritPadding py="md">
            <div className="items">
              {section?.items
                .filter(
                  (item) => !item.conditionalOn || item.conditionalOn(settings)
                )
                .map((item) => mapItem(item))}
            </div>

            {section?.renderExample && (
              <div className="mt-4">{section.renderExample()}</div>
            )}
          </Card.Section>
        </AppCard>
      ))}
    </Container>
  );
}
